/* eslint-disable react/no-array-index-key */
/* eslint-disable no-unused-vars */
import { useSelector } from "react-redux";
import React, { FC } from "react";
import { CnpForm, IProduct } from "../../../types";
import {
  getTotalAmountForLineItem,
  getTotalAmountForLineItemArray,
  getTotalVatAmountForLineItemArray,
  getVatAmountForLineItem,
} from "../../../utils/Calculation";
import "../LineItemInputForm/LineItem.scss";
import "./Review.css";
import deleteIcon from "../../../assets/icon-bin.svg";
import editIcon from "../../../assets/edit-icon.svg";
import addIcon from "../../../assets/icon-plus.svg";
import errorIcon from "../../../assets/icon-error.svg";
import { numberWithSpaces } from "../../../utils/utils";
import { getFromSessionStorage, StorageKeys } from "../../../utils/Storage";

type LineItemData = {
  lineItems: Array<IProduct>;
};

interface ReviewProps {
  data: CnpForm;
  updateData: (fields: Partial<LineItemData>) => void;
  canProceed: any;
  goTo: any;
}

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const Review: FC<ReviewProps> = ({ data, updateData, canProceed, goTo }) => {
  const merchantDetails = useSelector(
    // @ts-ignore
    (state) => state.authenticationReducer.merchantDetails,
  );
  const { customerInformation: customer } = data;
  const { complexBuilding, streetAddress, suburb, city, province, country, postalCode } = customer.billingAddress;

  const { lineItems } = data;
  const { dueDate, invoiceNumber, additionalNotes } = data.paymentTerms;
  const invoiceDueDate = new Date(dueDate);
  const invoiceInitDate = new Date();

  if (lineItems.length > 0) canProceed(true);
  else canProceed(false);

  const removeItem = (id: number) => {
    const filteredItems = lineItems.filter((item) => item.id !== id);
    updateData({ lineItems: filteredItems });
  };

  const customerDetailsAvailable = () => {
    return !complexBuilding && !streetAddress && !suburb && !city && !province && !country && !postalCode;
  };

  const total = getTotalAmountForLineItemArray(lineItems);
  const vat = getTotalVatAmountForLineItemArray(lineItems);
  return (
    <div className="review">
      <div className="merchant">
        <div className="colOne">
          <div className="content">
            <div className="name">{merchantDetails.companyName}</div>
            <div className="email">{merchantDetails.email}</div>
            <div className="phone">{merchantDetails.phoneNumber}</div>
            <div className="address">
              {`${merchantDetails.address.street1 ? `${merchantDetails.address.street1},` : ""} ${
                merchantDetails.address.street2 ? `,${merchantDetails.address.street2},` : ""
              }`}
              {`${merchantDetails.address.province ? `${merchantDetails.address.province},` : ""} ${
                merchantDetails.address.city ? `${merchantDetails.address.city},` : ""
              } ${merchantDetails.address.code ? `${merchantDetails.address.code},` : ""}`}
            </div>
          </div>
        </div>
        <div className="colTwo">
          <div className="content">
            <div className="label">Invoice Number</div>
            <div className="value">{invoiceNumber || getFromSessionStorage(StorageKeys.INVOICE_REFERENCE)}</div>
            {merchantDetails.vatNumber && <div className="labelDate">VAT Number</div>}
            {merchantDetails.vatNumber && <div className="value">{merchantDetails.vatNumber}</div>}
            <div className="labelDate">Invoice Date</div>
            <div className="value">{`${invoiceInitDate.getDate()} ${
              months[invoiceInitDate.getMonth()]
            } ${invoiceInitDate.getFullYear()}`}</div>
          </div>
        </div>
      </div>
      <div className="divider" />
      <div className="line-items">
        <div className="content">
          <div className="header">
            <div className="label">Line Items</div>
            <div className="d-flex">
              {lineItems.length > 0 && <img className="icon" src={editIcon} alt="Edit" />}
              {lineItems.length === 0 && <img className="icon" src={addIcon} alt="Add" />}
              <div className="edit" onClick={() => goTo(4)} onKeyDown={() => {}} tabIndex={0} role="button">
                {lineItems.length > 0 ? "Edit" : "Add"}
              </div>
            </div>
          </div>
          {lineItems.length === 0 && (
            <div className="error-container mt-4">
              <img src={errorIcon} alt="Error Icon" />
              <div className="d-flex">
                <div>Please</div>
                <button type="button" onClick={() => goTo(4)}>
                  add line item
                </button>
                <div>in order to share invoice.</div>
              </div>
            </div>
          )}
          {lineItems.length > 0 && (
            <div className="line-item-container mt-4 text-bold text-size-16">
              <div className="line-item heading">Item Name</div>
              <div className="line-item heading">Description</div>
              <div className="line-item heading">Qty</div>
              <div className="line-item heading">VAT</div>
              <div className="line-item heading">Price (incl. VAT)</div>
            </div>
          )}
          {lineItems.map((item, index) => (
            <div className="line-item-container mt-1" key={index}>
              <div className="line-item name">{item.itemName}</div>
              <div className="line-item description">{item.description}</div>
              <div className="line-item quantity">{numberWithSpaces(item.lineItemQuantity)}</div>
              <div className="line-item vat">
                R&nbsp;
                {numberWithSpaces(getVatAmountForLineItem(item).toFixed(2))}
              </div>
              <div className="line-item price text-bold">
                R&nbsp;
                {numberWithSpaces(getTotalAmountForLineItem(item).toFixed(2))}
              </div>
              <div>
                <img
                  className="float-end"
                  src={deleteIcon}
                  alt="Delete Icon"
                  onClick={() => removeItem(item.id)}
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                  role="button"
                  tabIndex={0}
                  onKeyDown={() => {}}
                />
              </div>
            </div>
          ))}

          <div className="totals">
            <div className="subtotal">
              <div className="label">Subtotal</div>
              <div className="value">R&nbsp;{numberWithSpaces((total - vat).toFixed(2))}</div>
            </div>
            <div className="vat">
              <div className="label">VAT (15%)</div>
              <div className="value">R&nbsp;{numberWithSpaces(vat.toFixed(2))}</div>
            </div>
            <div className="due">
              <div className="label">Total Due</div>
              <div className="value">R&nbsp;{numberWithSpaces(total.toFixed(2))}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="divider" />
      <div className="payment-terms">
        <div className="header">
          <div className="label">Payment Terms</div>
          <div className="d-flex">
            <img className="icon" src={editIcon} alt="Edit" />
            <div className="edit" onClick={() => goTo(5)} onKeyDown={() => {}} tabIndex={0} role="button">
              Edit
            </div>
          </div>
        </div>
        <div className="date">
          <div className="label">Due Date</div>
          <div className="value">{`${invoiceDueDate.getDate()} ${
            months[invoiceDueDate.getMonth()]
          } ${invoiceDueDate.getFullYear()}`}</div>
        </div>
        {additionalNotes && (
          <div className="notes">
            <div className="label">Additional Notes</div>
            <div className="value">{additionalNotes}</div>
          </div>
        )}
      </div>
      <div className="divider" />
      <div className="customer-details">
        <div className="header">
          <div className="label">Customer Details</div>
          <div className="d-flex">
            <img className="icon" src={editIcon} alt="Edit" />
            <div className="edit" onClick={() => goTo(6)} onKeyDown={() => {}} tabIndex={0} role="button">
              Edit
            </div>
          </div>
        </div>
        <div className="details">
          <div className="name">
            <div className="label">Customer Name</div>
            <div className="value">{customer.customerName}</div>
          </div>
          <div className="email">
            <div className="label">Email</div>
            <div className="value">{customer.emailAddress}</div>
          </div>
          {customer.contactNumber && (
            <div className="phone">
              <div className="label">Number</div>
              <div className="value">{customer.contactNumber}</div>
            </div>
          )}
        </div>
        {!customerDetailsAvailable() && (
          <div className="address">
            <div className="main">Street Address</div>
            <div className="more">
              {`${complexBuilding} ${streetAddress} ${suburb}
              ${city} ${province} ${country} ${postalCode}`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Review;
